import { ViewerData } from "../../../../models/viewer/ViewerData"
import React, { useCallback, useEffect, useMemo, useState } from "react"
import usePageInformation from "../../../viewer/hooks/usePageInformation"
import useViewerPreProcess from "../../../viewer/hooks/useViewerPreProcess"
import { range } from "lodash"
import ViewerWrapper from "../../../viewer/ViewerWrapper"
import Page from "../../../viewer/Page"
import styles from "./Viewer.module.scss"
import UserHeader from "../../../viewer/UserHeader"
import { Novel } from "../../../../models/tieup/novel/Novel"
import Drawer from "../../../viewer/Drawer"
import {
  ViewerBackgroundType,
  ViewerFontSize,
  ViewerFontType,
} from "../../../../../types"
import { useDisplaySettings } from "../../../viewer/hooks/useDisplaySettings"
import clsx from "clsx"
import Slider from "../../../viewer/Slider"
import UserFooter from "../../../viewer/UserFooter"
import routes from "../../../../../routes"
import { useRouter } from "next/router"
import { useVolumeViewerNavigation } from "../../../viewer/v2/VolumeViewer/useVolumeViewerNavigation"
import { PageLinkType } from "../../../../models/PageLInkType"
import Link from "next/link"
import FinishReadingDialog from "../../../viewer/FinishReadingDialog"

type Props = {
  chapterId: number
  viewer: ViewerData
  novel: Novel
  isAndroid?: boolean
}

export const Viewer = ({
  chapterId,
  viewer,
  novel,
  isAndroid = false,
}: Props): JSX.Element => {
  const router = useRouter()

  const [isInitialized, setIsInitialized] = useState(false)
  const [pageElement, setPageElement] = useState<HTMLDivElement | null>(null)
  const [viewerElement, setViewerElement] = useState<HTMLDivElement | null>(
    null,
  )
  const [isOpenDrawer, setIsOpenDrawer] = useState(false)
  const [showToolbar, setShowToolbar] = useState(false)
  const [index, setIndex] = useState<number>(0)
  const [showEndZone, setShowEndZone] = useState(false)

  const {
    backgroundType = ViewerBackgroundType.White,
    fontType = isAndroid ? ViewerFontType.Gothic : ViewerFontType.Mincho,
    fontSize = ViewerFontSize.Medium,
    fontSizeNum,
    handleChangeBackgroundType,
    handleChangeFontType,
    handleChangeFontSize,
  } = useDisplaySettings(isAndroid)

  const handleToggleDrawer = useCallback(() => {
    setIsOpenDrawer((v) => !v)
  }, [])
  const handleResize = useCallback(() => {
    setIsInitialized(false)
  }, [])

  const { pageInformation } = usePageInformation(viewerElement, pageElement)

  const { calculateRef, getPageData, maxPage } = useViewerPreProcess(
    viewer.lines,
    viewer.meta,
    pageInformation,
    setIsInitialized,
  )

  const pages = useMemo(() => {
    const pageIndexes = range(0, maxPage, 1).map((x) => [x])

    return pageIndexes.map((x) => x.map((y) => getPageData(y)))
  }, [getPageData, maxPage])
  const isFirstPage = index === 0
  const isLastPage =
    pages.length === 0
      ? index === pages.length // pages.lengthのが0の場合、0が最終ページになるので-1しない
      : index === pages.length - 1

  const navigatePage = useCallback(
    (indexNum: number) => {
      setIndex(indexNum)
    },
    [setIndex],
  )

  const publicChapters = novel.chapters?.filter((v) => v.is_published) || []

  const nextChapter = (() => {
    const currentIndex = publicChapters.findIndex((v) => v.id === chapterId)
    if (
      currentIndex === undefined ||
      currentIndex === -1 ||
      currentIndex + 1 >= publicChapters.length
    ) {
      return
    }

    return publicChapters[currentIndex + 1]
  })()

  const prevChapter = (() => {
    const currentIndex = publicChapters.findIndex((v) => v.id === chapterId)
    if (
      currentIndex === undefined ||
      currentIndex === -1 ||
      currentIndex === 0
    ) {
      return
    }

    return publicChapters[currentIndex - 1]
  })()

  const toNext = () => {
    if (showEndZone) {
      return
    }

    if (!isLastPage) {
      navigatePage(index + 1)

      return
    }

    if (!nextChapter) {
      setShowEndZone(true)

      return
    }

    router.push(routes.viewerText(novel.id, nextChapter.id, false))
  }

  const toPrev = () => {
    if (showEndZone) {
      return
    }

    if (!isFirstPage) {
      navigatePage(index - 1)

      return
    }

    if (!prevChapter) {
      return
    }

    router.push(routes.viewerText(novel.id, prevChapter.id, true))
  }

  const toShowToolbar = () => {
    if (showEndZone) {
      return
    }

    setShowToolbar((v) => !v)
  }

  const { bind } = useVolumeViewerNavigation(
    viewerElement,
    toNext,
    toPrev,
    toShowToolbar,
  )

  /** ページ間遷移した際の初期化処理 */
  useEffect(() => {
    setIsInitialized(false)
    setIsOpenDrawer(false)

    // 前の章からの遷移では前の章の最後のページを表示
    if (router.query.type === PageLinkType.BACKWARD) {
      navigatePage(Math.max(pages.length - 1, 0))
    } else {
      navigatePage(0)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [router.asPath])

  useEffect(() => {
    if (!isInitialized) return
    // pages.lengthが0でindexが0の場合、ページを移動しない
    if (pages.length === 0 && index === 0) return

    if (index >= pages.length) {
      // 現在のページ番号がページ数よりも大きいため最後のページに移動
      navigatePage(pages.length - 1)
    }
  }, [index, isInitialized, navigatePage, pages.length])

  return (
    <ViewerWrapper>
      <div
        ref={setViewerElement}
        className={`
          ${styles.viewer}
          ${backgroundType === ViewerBackgroundType.White ? styles.white : ""}
          ${backgroundType === ViewerBackgroundType.Black ? styles.black : ""}
        `}
        style={{
          fontSize: `${fontSizeNum}px`,
          lineHeight: `${fontSizeNum * 2}px`,
        }}
      >
        {isInitialized ? (
          <>
            <div
              className={`${styles.toolbar} ${styles.toolbarTop} ${
                showToolbar && styles.toolbarVisible
              }`}
            >
              <UserHeader
                title={novel.title || ""}
                onClickBack={() => {
                  router.push(routes.creatorNovelsDetail(novel.id))
                }}
              />
            </div>
            {showToolbar && isFirstPage && prevChapter && (
              <div className={clsx([styles.ClickArea, styles.PrevBtn])}>
                <div className={styles.ClickAreaInner}>
                  <Link href={routes.viewerText(novel.id, prevChapter.id)}>
                    <a>前の章へ</a>
                  </Link>
                </div>
              </div>
            )}
            <div
              {...bind()}
              className={`
                ${styles.pageContainer}
                ${fontType === ViewerFontType.Gothic ? styles.gothic : ""}
                ${
                  fontType === ViewerFontType.Mincho && isAndroid
                    ? styles.mincho
                    : ""
                }
              `}
            >
              {pages[index]?.map((page) => (
                <Page
                  {...page}
                  backgroundType={backgroundType}
                  key={`page${page.beginIndex}-${page.endIndex}`}
                />
              ))}
            </div>
            {isLastPage && showToolbar && nextChapter && (
              <div className={clsx([styles.ClickArea, styles.NextBtn])}>
                <div className={styles.ClickAreaInner}>
                  <Link href={routes.viewerText(novel.id, nextChapter.id)}>
                    <a>次の章へ</a>
                  </Link>
                </div>
              </div>
            )}
            <div
              className={clsx([
                styles.toolbar,
                styles.toolbarBottom,
                showToolbar && styles.toolbarVisible,
              ])}
            >
              <div className={styles.slider}>
                <Slider
                  index={index}
                  total={pages.length}
                  onChangeSlider={navigatePage}
                />
              </div>
              <UserFooter
                title={novel.title || ""}
                onClickOpenDrawer={handleToggleDrawer}
              />
            </div>
            <Drawer
              isOpen={isOpenDrawer}
              title={novel.title || ""}
              creators={[]}
              shareUrl={""}
              backgroundType={backgroundType}
              fontType={fontType}
              fontSize={fontSize}
              onChangeBackgroundType={handleChangeBackgroundType}
              onChangeFontType={handleChangeFontType}
              onChangeFontSize={handleChangeFontSize}
              onClose={handleToggleDrawer}
              onResize={handleResize}
              showShare={false}
              chapters={novel.chapters
                ?.filter((v) => v.is_published)
                .map((v) => {
                  return {
                    id: v.id.toString(),
                    label: v.title,
                    link: `/novels/${novel.id}/chapters/${v.id}`,
                  }
                })}
              currentChapterId={chapterId.toString()}
            />
          </>
        ) : (
          <div className={styles.pageContainer}>
            <h1>notInitialized</h1>
            <Page
              data={viewer.lines}
              ref={calculateRef}
              pageElement={setPageElement}
            />
          </div>
        )}
        {showEndZone && (
          <FinishReadingDialog
            showEndZone={showEndZone}
            novelistDisplayName={novel.user.display_name}
            novelistIcon={novel.user.icon_url || "/img/user-icon.png"}
            handleOpenEndZone={() => setShowEndZone(true)}
            handleCloseEndZone={() => setShowEndZone(false)}
          />
        )}
      </div>
    </ViewerWrapper>
  )
}
